<script setup>
import AdminLayout from "@/Layouts/AdminLayout/AdminLayout.vue"
import TreeNode from "@/Components/Binary/TreeNode.vue"
import { ref, reactive, computed, onMounted, onUnmounted, nextTick, watch } from "vue"
import { usePage, router } from "@inertiajs/vue3"
import axios from "axios"

const props = defineProps({
    tree:         { type: Object, required: true },
    searchUser:   { type: Object, default: null },
    searchTerm:   { type: String, default: '' },
    currentDepth: { type: Number, default: 3 },
})

const page           = usePage()
const currencySymbol = computed(() => page.props.currencySymbol || '$')

// DOM refs
const treeContainer = ref(null)
const treeViewport  = ref(null)

// Transform
const transform  = reactive({ x: 0, y: 0, scale: 1 })
const isDragging = ref(false)
const dragStart  = reactive({ x: 0, y: 0, tx: 0, ty: 0 })
const pinchDist  = ref(null)

// Tree state
const expandedNodes  = reactive({})
const loadedNodes    = reactive({})
const loadingNodes   = reactive({})
const animatingNodes = reactive({})

// Modal
const modal = reactive({ visible: false, node: null, loading: false })

// SVG connectors
const connectors = ref([])

// Search
const searchQuery = ref('')
const isSearching = ref(false)

// Merged reactive tree
const rootNode = computed(() => mergeNodeData(props.tree))

function mergeNodeData(node, seen = new Set()) {
    if (!node) return null
    if (seen.has(node.id)) {
        return {
            ...node,
            _expanded: false,
            left_child: null,
            right_child: null,
            has_children: true,
        }
    }

    const nextSeen = new Set(seen)
    nextSeen.add(node.id)

    const override = loadedNodes[node.id]
    const merged   = override ? { ...node, ...override } : { ...node }
    merged._expanded   = expandedNodes[merged.id] !== false
    merged.left_child   = merged._expanded ? mergeNodeData(merged.left_child, nextSeen) : null
    merged.right_child  = merged._expanded ? mergeNodeData(merged.right_child, nextSeen) : null
    return merged
}

const fmt      = v => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(parseFloat(v) || 0)
const fmtN     = v => new Intl.NumberFormat('en-US').format(parseInt(v) || 0)
const initials = n => n ? n.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : '?'

// Node click: lazy-load children or toggle
async function handleNodeClick(node) {
    const id = node.id
    if (loadingNodes[id]) return
    if (expandedNodes[id] === true)  { expandedNodes[id] = false; scheduleConnectors(); return }
    if (expandedNodes[id] === false) { expandedNodes[id] = true;  scheduleConnectors(); return }
    if (!node.has_children && !node.left_child && !node.right_child) { openModal(node); return }

    loadingNodes[id] = animatingNodes[id] = true
    try {
        const { data } = await axios.get(`/admin/binary/tree/children/${id}`)
        if (data.success) {
            loadedNodes[id] = {
                left_child:   data.node.left_child,
                right_child:  data.node.right_child,
                has_children: data.node.has_children,
            }
        }
        expandedNodes[id] = true
    } catch (e) { console.error(e) }
    finally {
        loadingNodes[id] = false
        setTimeout(() => { animatingNodes[id] = false }, 500)
        scheduleConnectors()
    }
}

async function openModal(node) {
    modal.visible = true; modal.loading = true; modal.node = node
    try {
        const { data } = await axios.get(`/admin/binary/tree/details/${node.id}`)
        if (data.success) modal.node = data.node
    } catch (e) { console.error(e) }
    finally { modal.loading = false }
}
const closeModal = () => { modal.visible = false; modal.node = null }

// SVG connectors
let _raf = null
function scheduleConnectors() {
    if (_raf) cancelAnimationFrame(_raf)
    _raf = requestAnimationFrame(drawConnectors)
}

function offsetsFrom(el, ancestor) {
    let x = 0, y = 0, cur = el
    while (cur && cur !== ancestor) { x += cur.offsetLeft; y += cur.offsetTop; cur = cur.offsetParent }
    return { x, y, w: el.offsetWidth, h: el.offsetHeight }
}

function drawConnectors() {
    if (!treeViewport.value) return
    const vp = treeViewport.value, lines = []
    vp.querySelectorAll('[data-node-id]').forEach(card => {
        const pid = card.dataset.parentId
        if (!pid) return
        const parent = vp.querySelector(`[data-node-id="${pid}"]`)
        if (!parent) return
        const p = offsetsFrom(parent, vp), c = offsetsFrom(card, vp)
        const px = p.x + p.w / 2, py = p.y + p.h
        const cx = c.x + c.w / 2, cy = c.y
        const my = (py + cy) / 2
        lines.push({ id: `${pid}-${card.dataset.nodeId}`, d: `M ${px} ${py} C ${px} ${my}, ${cx} ${my}, ${cx} ${cy}`, pos: card.dataset.position })
    })
    connectors.value = lines
}

// Zoom / Pan
const MIN_SCALE = 0.12, MAX_SCALE = 3

function zoomIn()  { applyZoom(transform.scale + 0.15) }
function zoomOut() { applyZoom(transform.scale - 0.15) }

function applyZoom(raw, ox, oy) {
    const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, raw))
    if (ox !== undefined) {
        const r = next / transform.scale - 1
        transform.x -= ox * r; transform.y -= oy * r
    }
    transform.scale = next
    scheduleConnectors()
}

function centerTree() {
    transform.scale = 1
    transform.x = 0
    transform.y = 0
    scheduleConnectors()
}

function expandAll(node) {
    if (!node) return
    expandedNodes[node.id] = true
    expandAll(node.left_child); expandAll(node.right_child)
    scheduleConnectors()
}
function collapseAll(node) {
    if (!node) return
    if (node.id !== rootNode.value?.id) expandedNodes[node.id] = false
    collapseAll(node.left_child); collapseAll(node.right_child)
    scheduleConnectors()
}

// Mouse drag
function onMouseDown(e) {
    if (e.button !== 0) return
    isDragging.value = true
    dragStart.x = e.clientX; dragStart.y = e.clientY
    dragStart.tx = transform.x; dragStart.ty = transform.y
    e.preventDefault()
}
function onMouseMove(e) {
    if (!isDragging.value) return
    transform.x = dragStart.tx + (e.clientX - dragStart.x)
    transform.y = dragStart.ty + (e.clientY - dragStart.y)
    scheduleConnectors()
}
function onMouseUp() { isDragging.value = false }

// Wheel zoom toward cursor
function onWheel(e) {
    e.preventDefault()
    if (!treeContainer.value) return
    const r = treeContainer.value.getBoundingClientRect()
    const ox = (e.clientX - r.left - r.width  / 2) - transform.x
    const oy = (e.clientY - r.top  - r.height / 2) - transform.y
    applyZoom(transform.scale + (e.deltaY > 0 ? -0.12 : 0.12), ox, oy)
}

// Touch
function onTouchStart(e) {
    if (e.touches.length === 1) {
        isDragging.value = true
        dragStart.x = e.touches[0].clientX; dragStart.y = e.touches[0].clientY
        dragStart.tx = transform.x; dragStart.ty = transform.y
    } else if (e.touches.length === 2) {
        pinchDist.value = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY)
    }
}
function onTouchMove(e) {
    e.preventDefault()
    if (e.touches.length === 1 && isDragging.value) {
        transform.x = dragStart.tx + (e.touches[0].clientX - dragStart.x)
        transform.y = dragStart.ty + (e.touches[0].clientY - dragStart.y)
        scheduleConnectors()
    } else if (e.touches.length === 2 && pinchDist.value) {
        const d = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY)
        applyZoom(transform.scale * (d / pinchDist.value)); pinchDist.value = d
    }
}
function onTouchEnd() { isDragging.value = false; pinchDist.value = null }

// Search
const searchUserFunc = () => {
    if (!searchQuery.value.trim()) return
    
    isSearching.value = true
    router.get('/admin/binary/tree', { search: searchQuery.value }, {
        preserveState: false,
        onFinish: () => {
            isSearching.value = false
        }
    })
}

const resetSearch = () => {
    searchQuery.value = ''
    router.get('/admin/binary/tree', {}, {
        preserveState: false
    })
}

function clearReactiveMap(obj) {
    Object.keys(obj).forEach((k) => delete obj[k])
}

function resetTreeState() {
    clearReactiveMap(expandedNodes)
    clearReactiveMap(loadedNodes)
    clearReactiveMap(loadingNodes)
    clearReactiveMap(animatingNodes)
    connectors.value = []

    if (props.tree?.id) {
        expandedNodes[props.tree.id] = true
    }

    transform.scale = 1
    transform.x = 0
    transform.y = 0
    nextTick(scheduleConnectors)
}

// Lifecycle
const ro = ref(null)
onMounted(() => {
    if (props.searchTerm) {
        searchQuery.value = props.searchTerm
    }

    if (props.tree?.id) expandedNodes[props.tree.id] = true
    nextTick(scheduleConnectors)
    ro.value = new ResizeObserver(scheduleConnectors)
    if (treeViewport.value) ro.value.observe(treeViewport.value)
    window.addEventListener('mouseup',   onMouseUp)
    window.addEventListener('mousemove', onMouseMove)
})
onUnmounted(() => {
    ro.value?.disconnect()
    window.removeEventListener('mouseup',   onMouseUp)
    window.removeEventListener('mousemove', onMouseMove)
})
watch(() => props.tree, () => nextTick(scheduleConnectors), { deep: true })
watch(() => props.tree?.id, () => {
    resetTreeState()
})
</script>

<template>
    <AdminLayout title="Binary Tree Visualization">
        <div class="w-full px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10">
            <div class="max-w-[1400px] mx-auto space-y-6">

                <!-- Title + legend row -->
                <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                        <h1 class="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">Binary Network Tree</h1>
                        <p class="mt-2 text-sm text-gray-700 dark:text-slate-400">Click nodes to expand downline · Drag to pan · Scroll to zoom</p>
                    </div>
                    <div class="flex items-center gap-2 flex-wrap">
                        <span class="legend-chip bg-green-500/15 text-green-300 border-green-500/30">
                            <span class="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>Active
                        </span>
                        <span class="legend-chip bg-red-500/15 text-red-300 border-red-500/30">
                            <span class="w-1.5 h-1.5 rounded-full bg-red-400"></span>Inactive
                        </span>
                        <span class="legend-chip bg-amber-500/15 text-amber-300 border-amber-500/30">
                            <span class="w-1.5 h-1.5 rounded-full bg-amber-400"></span>Empty
                        </span>
                    </div>
                </div>

                <!-- Search Bar -->
                <div class="bg-gradient-to-br from-slate-900/90 to-slate-800/70 backdrop-blur-xl border border-slate-700/40 rounded-2xl overflow-hidden p-5">
                    <div class="flex flex-col sm:flex-row gap-3">
                        <input
                            v-model="searchQuery"
                            type="text"
                            placeholder="Search by name, email or user ID..."
                            class="flex-1 rounded-lg bg-slate-800/60 border border-slate-600/70 text-white placeholder-slate-400 p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            @keyup.enter="searchUserFunc"
                        >
                        <button
                            @click="searchUserFunc"
                            :disabled="isSearching || !searchQuery.trim()"
                            class="px-5 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg transition-colors whitespace-nowrap"
                        >
                            {{ isSearching ? 'Searching...' : 'Search' }}
                        </button>
                        <button
                            v-if="searchQuery || props.searchUser"
                            @click="resetSearch"
                            class="px-5 py-3 bg-slate-700 hover:bg-slate-600 text-slate-200 font-medium rounded-lg transition-colors whitespace-nowrap"
                        >
                            Reset
                        </button>
                    </div>
                    <p v-if="props.searchUser" class="mt-3 text-sm text-green-400">
                        ✓ Showing tree for: {{ props.searchUser.display_name || props.searchUser.name }} ({{ props.searchUser.email }})
                    </p>
                </div>

                <!-- Main glass card: controls + canvas -->
                <div class="bg-gradient-to-br from-slate-900/90 to-slate-800/70 backdrop-blur-xl border border-slate-700/40 rounded-2xl overflow-hidden">

        <!-- ══ Controls bar ══════════════════════════════════════════════════════ -->
        <div class="border-b border-slate-700/60 px-4 sm:px-5 py-3 flex flex-wrap items-center gap-2 bg-slate-800/40">

            <!-- Zoom pill -->
            <div class="flex items-center rounded-xl overflow-hidden border border-slate-600/70 shadow-sm shrink-0">
                <button @click="zoomOut" class="ctrl-btn rounded-none border-0 border-r border-slate-600/70" title="Zoom Out">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M20 12H4"/>
                    </svg>
                </button>
                <span class="px-3 text-xs font-mono font-bold text-slate-200 min-w-[3.5rem] text-center bg-slate-800/60 select-none">
                    {{ Math.round(transform.scale * 100) }}%
                </span>
                <button @click="zoomIn" class="ctrl-btn rounded-none border-0 border-l border-slate-600/70" title="Zoom In">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 4v16m8-8H4"/>
                    </svg>
                </button>
            </div>

            <!-- Center -->
            <button @click="centerTree" class="ctrl-btn gap-1.5" title="Center Tree">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="3" stroke-width="2"/>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 2v3m0 14v3M2 12h3m14 0h3"/>
                </svg>
                <span class="hidden sm:inline text-xs font-semibold">Center</span>
            </button>

            <div class="w-px h-5 bg-slate-600/70 shrink-0"></div>

            <!-- Expand All -->
            <button @click="expandAll(rootNode)" class="ctrl-btn gap-1.5" title="Expand All">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                </svg>
                <span class="hidden sm:inline text-xs font-semibold">Expand All</span>
            </button>

            <!-- Collapse -->
            <button @click="collapseAll(rootNode)" class="ctrl-btn gap-1.5" title="Collapse All">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"/>
                </svg>
                <span class="hidden sm:inline text-xs font-semibold">Collapse</span>
            </button>

            <div class="ml-auto hidden md:flex items-center gap-1.5 text-xs text-slate-500">
                <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                Pinch to zoom on mobile · Click node to expand or view details
            </div>
        </div>

        <!-- ══ Canvas ════════════════════════════════════════════════════════════ -->
        <div class="p-4 sm:p-5">
            <div ref="treeContainer" class="tree-canvas"
                 :style="{ cursor: isDragging ? 'grabbing' : 'grab' }"
                 @mousedown="onMouseDown"
                 @wheel.prevent="onWheel"
                 @touchstart.passive="onTouchStart"
                 @touchmove.prevent="onTouchMove"
                 @touchend="onTouchEnd">

                <!-- Dot-grid background -->
                <svg class="absolute inset-0 w-full h-full pointer-events-none" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                        <pattern id="treeDots" x="0" y="0" width="24" height="24" patternUnits="userSpaceOnUse">
                            <circle cx="1" cy="1" r="1" class="fill-gray-300 dark:fill-slate-700"/>
                        </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#treeDots)"/>
                </svg>

                <div ref="treeViewport"
                     class="absolute will-change-transform select-none"
                     style="top:50%;left:50%;transform-origin:0 0;"
                     :style="{
                         transform: `translate(calc(-50% + ${transform.x}px), calc(-50% + ${transform.y}px)) scale(${transform.scale})`,
                         transition: isDragging ? 'none' : 'transform 0.08s ease-out',
                     }">

                    <!-- SVG connectors -->
                    <svg class="absolute inset-0 pointer-events-none overflow-visible"
                         style="width:100%;height:100%;z-index:0">
                        <defs>
                            <filter id="fgl" x="-80%" y="-80%" width="260%" height="260%">
                                <feGaussianBlur in="SourceGraphic" stdDeviation="2.5" result="b"/>
                                <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
                            </filter>
                            <filter id="fgr" x="-80%" y="-80%" width="260%" height="260%">
                                <feGaussianBlur in="SourceGraphic" stdDeviation="2.5" result="b"/>
                                <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
                            </filter>
                        </defs>
                        <path v-for="ln in connectors" :key="ln.id"
                              :d="ln.d" fill="none" stroke-linecap="round"
                              :stroke="ln.pos === 'left' ? '#22c55e' : '#a855f7'"
                              :filter="ln.pos === 'left' ? 'url(#fgl)' : 'url(#fgr)'"
                              stroke-width="2" opacity="0.75"/>
                    </svg>

                    <!-- Tree content -->
                    <div class="relative z-10 flex justify-center items-start py-12 px-20 min-w-max">
                        <TreeNode
                            v-if="rootNode"
                            :node="rootNode"
                            :is-root="true"
                            :loading-nodes="loadingNodes"
                            :animating-nodes="animatingNodes"
                            :currency-symbol="currencySymbol"
                            @node-click="handleNodeClick"
                            @node-info="openModal"
                            @connectors-update="scheduleConnectors"
                        />
                        <div v-else class="flex flex-col items-center justify-center gap-4 text-gray-400 dark:text-gray-600 py-32 px-24">
                            <svg class="w-20 h-20 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
                            </svg>
                            <p class="font-bold text-xl">No binary tree data</p>
                            <p class="text-sm text-center max-w-xs opacity-70">Select a user to view their binary tree</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

                </div><!-- /glass card -->
            </div><!-- /max-w container -->
        </div><!-- /outer padding -->

        <!-- ══ Detail Modal ══════════════════════════════════════════════════════ -->
        <Teleport to="body">
            <Transition name="modal-pop">
                <div v-if="modal.visible"
                     class="fixed inset-0 z-[999] flex items-center justify-center p-4"
                     @click.self="closeModal">
                    <div class="absolute inset-0 bg-black/70 backdrop-blur-md" @click="closeModal"></div>

                    <div class="relative z-10 w-full max-w-md bg-white dark:bg-slate-800 rounded-3xl
                                shadow-2xl overflow-hidden ring-1 ring-black/10 dark:ring-white/10">

                        <!-- Skeleton -->
                        <div v-if="modal.loading" class="p-8 space-y-5">
                            <div class="flex items-center gap-4">
                                <div class="w-16 h-16 rounded-2xl bg-slate-200 dark:bg-slate-700 animate-pulse shrink-0"></div>
                                <div class="flex-1 space-y-2.5">
                                    <div class="h-4 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse w-3/4"></div>
                                    <div class="h-3 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse w-1/2"></div>
                                </div>
                            </div>
                            <div class="grid grid-cols-3 gap-3">
                                <div v-for="i in 3" :key="i" class="h-16 bg-slate-200 dark:bg-slate-700 rounded-2xl animate-pulse"></div>
                            </div>
                            <div class="space-y-3">
                                <div v-for="i in 4" :key="i" class="h-3 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse"></div>
                            </div>
                        </div>

                        <template v-else-if="modal.node">
                            <!-- Gradient header -->
                            <div class="relative px-6 pt-8 pb-12 overflow-hidden"
                                 style="background:linear-gradient(135deg,#0f172a 0%,#1e3a5f 52%,#312e81 100%)">
                                <div class="absolute -top-8 -right-8 w-36 h-36 rounded-full bg-white/5 pointer-events-none"></div>
                                <div class="absolute -bottom-4 -left-6 w-24 h-24 rounded-full bg-white/5 pointer-events-none"></div>

                                <button @click="closeModal"
                                    class="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center
                                           rounded-full bg-white/15 hover:bg-white/25 text-white/80 hover:text-white transition-all">
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/>
                                    </svg>
                                </button>

                                <div class="flex items-start gap-4 relative z-10">
                                    <div class="relative shrink-0">
                                        <div class="w-[4.5rem] h-[4.5rem] rounded-2xl overflow-hidden ring-2 ring-white/20 shadow-xl">
                                            <img v-if="modal.node.avatar_url" :src="modal.node.avatar_url" :alt="modal.node.name" class="w-full h-full object-cover"/>
                                            <div v-else class="w-full h-full flex items-center justify-center text-2xl font-black text-white"
                                                 style="background:linear-gradient(135deg,#3b82f6,#8b5cf6)">
                                                {{ initials(modal.node.display_name || modal.node.name) }}
                                            </div>
                                        </div>
                                        <span :class="['absolute -bottom-1.5 -right-1.5 flex items-center justify-center w-6 h-6 rounded-full border-2 border-slate-900 shadow',
                                                       modal.node.status === 'active' ? 'bg-green-500' : 'bg-red-500']">
                                            <svg v-if="modal.node.status === 'active'" class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                <path fill-rule="evenodd" clip-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                                            </svg>
                                            <svg v-else class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                <path fill-rule="evenodd" clip-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"/>
                                            </svg>
                                        </span>
                                    </div>

                                    <div class="text-white min-w-0 pt-1">
                                        <h2 class="text-lg font-black leading-tight truncate pr-8">{{ modal.node.display_name || modal.node.name }}</h2>
                                        <p class="text-sm text-blue-200/80 truncate mt-0.5">{{ modal.node.email }}</p>
                                        <div class="flex items-center gap-2 mt-2 flex-wrap">
                                            <span class="text-xs font-mono bg-white/10 text-blue-100 px-2.5 py-0.5 rounded-full">#{{ modal.node.uid }}</span>
                                            <span :class="['text-xs font-bold px-2.5 py-0.5 rounded-full',
                                                modal.node.status === 'active'
                                                    ? 'bg-green-500/20 text-green-300 ring-1 ring-green-500/40'
                                                    : 'bg-red-500/20 text-red-300 ring-1 ring-red-500/40']">
                                                {{ modal.node.status === 'active' ? '● Active' : '● Inactive' }}
                                            </span>
                                        </div>
                                            <p v-if="modal.node.status !== 'active'" class="mt-3 text-xs font-semibold uppercase tracking-[0.22em] text-red-200/80">
                                                Inactive accounts still show tree information and volumes.
                                            </p>
                                    </div>
                                </div>
                            </div>

                            <!-- Floating stat cards -->
                            <div class="px-5 -mt-6 grid grid-cols-3 gap-2.5 relative z-10">
                                <div class="modal-stat-card">
                                    <p class="modal-stat-label">Package</p>
                                    <p class="modal-stat-value text-blue-600 dark:text-blue-400">{{ currencySymbol }}{{ fmt(modal.node.package_amount) }}</p>
                                </div>
                                <div class="modal-stat-card">
                                    <p class="modal-stat-label">L. Team</p>
                                    <p class="modal-stat-value text-green-600 dark:text-green-400">{{ fmtN(modal.node.left_team_count) }}</p>
                                </div>
                                <div class="modal-stat-card">
                                    <p class="modal-stat-label">R. Team</p>
                                    <p class="modal-stat-value text-purple-600 dark:text-purple-400">{{ fmtN(modal.node.right_team_count) }}</p>
                                </div>
                            </div>

                            <!-- Detail rows -->
                            <div class="px-5 pt-5 pb-6 space-y-1.5">
                                <div v-for="row in [
                                    {label:'Sponsor',     value: modal.node.sponsor_name || '—'},
                                    {label:'Sponsor UID', value: modal.node.sponsor_uid ? '#'+modal.node.sponsor_uid : '—'},
                                    {label:'Joined',      value: modal.node.joined_at   || '—'},
                                    {label:'Position',    value: modal.node.binary_position || '—'},
                                ]" :key="row.label"
                                    class="flex items-center justify-between py-2.5 px-1 border-b border-gray-100 dark:border-slate-700/60 last:border-0">
                                    <span class="text-xs font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">{{ row.label }}</span>
                                    <span class="text-sm font-bold text-gray-900 dark:text-white">{{ row.value }}</span>
                                </div>

                                <!-- Volume cards -->
                                <div class="pt-3 grid grid-cols-2 gap-3">
                                    <div class="rounded-2xl p-3.5 border bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-100 dark:border-green-800/40">
                                        <p class="text-xs font-semibold uppercase tracking-wide text-green-600 dark:text-green-400 mb-1">Left Volume</p>
                                        <p class="text-base font-black text-green-700 dark:text-green-300">{{ currencySymbol }}{{ fmt(modal.node.left_leg_volume) }}</p>
                                    </div>
                                    <div class="rounded-2xl p-3.5 border bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 border-purple-100 dark:border-purple-800/40">
                                        <p class="text-xs font-semibold uppercase tracking-wide text-purple-600 dark:text-purple-400 mb-1">Right Volume</p>
                                        <p class="text-base font-black text-purple-700 dark:text-purple-300">{{ currencySymbol }}{{ fmt(modal.node.right_leg_volume) }}</p>
                                    </div>
                                </div>

                                <!-- Pairing bonus -->
                                <div class="mt-2 rounded-2xl p-4 flex items-center justify-between"
                                     style="background:linear-gradient(135deg,#2563eb,#7c3aed)">
                                    <div>
                                        <p class="text-xs font-semibold text-blue-200 uppercase tracking-wide mb-0.5">Total Pairing Bonus</p>
                                        <p class="text-2xl font-black text-white leading-none">{{ currencySymbol }}{{ fmt(modal.node.total_pairing_bonus) }}</p>
                                    </div>
                                    <div class="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                                        <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </Transition>
        </Teleport>

    </AdminLayout>
</template>

<style>
/* Canvas — always dark (sits inside dark glass card) */
.tree-canvas {
    position: relative;
    height: 74vh;
    min-height: 520px;
    overflow: hidden;
    border-radius: 0.75rem;
    border: 1px solid rgba(51,65,85,0.6);
    background: linear-gradient(135deg, #0a0f1e 0%, #0d1d3b 45%, #100d26 100%);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.04);
    user-select: none;
}

/* Control button — dark theme */
.ctrl-btn {
    display: inline-flex;
    align-items: center;
    padding: 0.4rem 0.7rem;
    border-radius: 0.625rem;
    background: rgba(30,41,59,0.8);
    border: 1px solid #334155;
    color: #94a3b8;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    box-shadow: 0 1px 2px rgba(0,0,0,0.2);
    user-select: none;
    white-space: nowrap;
}
.ctrl-btn:hover {
    background: rgba(30,58,95,0.9);
    border-color: #3b82f6;
    color: #93c5fd;
    box-shadow: 0 0 0 3px rgba(59,130,246,0.15);
    transform: translateY(-1px);
}
.ctrl-btn:active { transform: scale(0.96) !important; }

/* Legend chip */
.legend-chip {
    display: inline-flex; align-items: center; gap: 0.375rem;
    padding: 0.35rem 0.7rem; border-radius: 9999px;
    font-size: 0.7rem; font-weight: 700;
    border-width: 1px; border-style: solid;
    backdrop-filter: blur(4px);
}

/* Modal stat card */
.modal-stat-card {
    background: white; border-radius: 0.875rem; padding: 0.75rem 0.5rem;
    text-align: center; box-shadow: 0 4px 16px rgba(0,0,0,0.1);
    border: 1px solid rgba(0,0,0,0.05);
}
.dark .modal-stat-card { background: #1e293b; border-color: rgba(255,255,255,0.06); box-shadow: 0 4px 16px rgba(0,0,0,0.3); }
.modal-stat-label { font-size: 0.6rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #94a3b8; margin-bottom: 0.25rem; line-height: 1; }
.modal-stat-value { font-size: 0.8rem; font-weight: 900; line-height: 1.3; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* Modal animation */
.modal-pop-enter-active, .modal-pop-leave-active { transition: opacity 0.2s ease; }
.modal-pop-enter-from, .modal-pop-leave-to { opacity: 0; }

.children-expand-enter-active, .children-expand-leave-active { transition: max-height 0.4s ease, opacity 0.3s ease; }
.children-expand-enter-from, .children-expand-leave-to { max-height: 0; opacity: 0; }
</style>
